'use client';

import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Progress } from '@/shared/components/ui/progress';
import {
  Briefcase,
  Users,
  Brain,
  Phone,
  Database,
  TrendingUp,
  Clock,
  CheckCircle2,
  CalendarDays,
  ArrowRight,
} from 'lucide-react';
import useAdminDashboard from '@/modules/adminPortal/hooks/useAdminDashboard';

type RecentActivityItem = {
  id?: string;
  date?: string;
  time?: string;
  action?: string;
  message?: string;
  status?: string;
  candidateName?: string;
  performedBy?: {
    id?: string;
    name?: string;
    role?: string;
    email?: string;
  } | null;
  icon?: React.ComponentType<{ className?: string }>;
};

type TopHiringRoleItem = {
  title: string;
  applications: number;
  pendingATS: number;
  selected: number;
  rejected: number;
};

export default function AdminDashboard() {
  const { data, loading, error } = useAdminDashboard();

  const summary = data?.summary;
  const aiAnalysis = data?.aiAnalysis;
  const today = data?.today;
  const voiceAgent = data?.voiceAgent;

  const totalJobs = summary?.totalJobs ?? 0;
  const activeJobs = summary?.activeJobs ?? 0;
  const totalApplications = summary?.totalApplications ?? 0;
  const todaysApplications = summary?.todaysApplications ?? 0;
  const pendingATS = summary?.pendingATS ?? 0;
  const selected = summary?.selected ?? 0;
  const selectionRate = summary?.selectionRate ?? 0;

const analyzed = aiAnalysis?.analyzed ?? 0;
const shortlisted = aiAnalysis?.shortlisted ?? 0;
const rejected = aiAnalysis?.rejected ?? 0;

  const todayApplications = today?.applications ?? 0;
  const todayInterviews = today?.interviews ?? 0;
  const todayVoiceCalls = today?.voiceCalls ?? 0;

  const totalCalls = voiceAgent?.totalCalls ?? 0;
  const completedCalls = voiceAgent?.completed ?? 0;
  const successRate = voiceAgent?.successRate ?? 0;

  const topJobs: TopHiringRoleItem[] = (data?.topHiringRoles ?? []).map((item: any) => ({
    title: item?.role ?? '',
    applications: item?.applications ?? 0,
    pendingATS: item?.pending ?? 0,
    selected: item?.selected ?? 0,
    rejected: item?.rejected ?? 0,
  }));

  const recentActivity: RecentActivityItem[] = Array.isArray(data?.recentActivity)
    ? data.recentActivity
    : [];

  const activeJobsPercentage = totalJobs > 0 ? (activeJobs / totalJobs) * 100 : 0;

  const getActivityIcon = (activity: RecentActivityItem) => {
    const action = activity?.action?.toLowerCase() ?? activity?.message?.toLowerCase() ?? '';
    const status = activity?.status?.toLowerCase() ?? '';

    if (action.includes('ai') || action.includes('ats') || action.includes('screening')) {
      return Brain;
    }
    if (
      action.includes('hired') ||
      action.includes('selected') ||
      status.includes('hired') ||
      status.includes('selected')
    ) {
      return CheckCircle2;
    }
    if (action.includes('applied') || action.includes('application')) {
      return Users;
    }
    return Clock;
  };


  if (error && !data) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p className="font-medium text-red-600">Failed to load dashboard</p>
            <p className="mt-2 break-words text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-4 sm:space-y-6">
      <div className="flex min-w-0 flex-col gap-4 sm:gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="break-words text-2xl font-bold tracking-tight sm:text-3xl">
            Admin Dashboard
          </h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Overview of recruitment platform
          </p>
        </div>

      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="min-w-0 transition-shadow hover:shadow-md">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Active Jobs</p>
                <p className="mt-1 text-2xl font-bold sm:text-3xl">{activeJobs}</p>
                <p className="mt-1 text-xs text-muted-foreground">of {totalJobs} total</p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:h-12 sm:w-12">
                <Briefcase className="h-5 w-5 text-blue-600 sm:h-6 sm:w-6" />
              </div>
            </div>
            <Progress value={activeJobsPercentage} className="mt-4 h-1" />
          </CardContent>
        </Card>

        <Card className="min-w-0 transition-shadow hover:shadow-md">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Total Applications</p>
                <p className="mt-1 text-2xl font-bold sm:text-3xl">{totalApplications}</p>
                <p className="mt-1 text-xs text-blue-600">+{todaysApplications} today</p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 sm:h-12 sm:w-12">
                <Users className="h-5 w-5 text-green-600 sm:h-6 sm:w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 transition-shadow hover:shadow-md">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Pending ATS</p>
                <p className="mt-1 text-2xl font-bold sm:text-3xl">{pendingATS}</p>
                <p className="mt-1 text-xs text-yellow-600">Awaiting analysis</p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:h-12 sm:w-12">
                <Clock className="h-5 w-5 text-yellow-600 sm:h-6 sm:w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 transition-shadow hover:shadow-md">
          <CardContent className="p-4 sm:pt-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground">Selection Rate</p>
                <p className="mt-1 text-2xl font-bold sm:text-3xl">
                  {Number(selectionRate).toFixed(1)}%
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{selected} hired</p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-100 sm:h-12 sm:w-12">
                <TrendingUp className="h-5 w-5 text-purple-600 sm:h-6 sm:w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="min-w-0 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4 sm:pt-6">
            <div className="mb-4 flex items-center gap-2">
              <Brain className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
              <span className="font-semibold">Application's Stats</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center sm:gap-4">
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{analyzed}</p>
                <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Analyzed</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{shortlisted}</p>
                <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Shortlisted</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{rejected}</p>
                <p className="mt-1 text-[11px] text-blue-100 sm:text-xs">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4 sm:pt-6">
            <div className="mb-4 flex items-center gap-2">
              <Phone className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
              <span className="font-semibold">Voice Agent</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center sm:gap-4">
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{totalCalls}</p>
                <p className="mt-1 text-[11px] text-green-100 sm:text-xs">Total Calls</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{completedCalls}</p>
                <p className="mt-1 text-[11px] text-green-100 sm:text-xs">Completed</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{Number(successRate).toFixed(0)}%</p>
                <p className="mt-1 text-[11px] text-green-100 sm:text-xs">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0 bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-4 sm:pt-6">
            <div className="mb-4 flex items-center gap-2">
              <CalendarDays className="h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
              <span className="font-semibold">Today</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center sm:gap-4">
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{todayApplications}</p>
                <p className="mt-1 text-[11px] text-orange-100 sm:text-xs">Applications</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{todayInterviews}</p>
                <p className="mt-1 text-[11px] text-orange-100 sm:text-xs">Interviews</p>
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold sm:text-3xl">{todayVoiceCalls}</p>
                <p className="mt-1 text-[11px] text-orange-100 sm:text-xs">Voice Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <CardTitle>Top Hiring Roles</CardTitle>
                <CardDescription className="mt-1">Jobs with most applications</CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild className="w-full sm:w-auto">
                <Link to="/admin/jobs">View All</Link>
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className="space-y-3">
              {topJobs.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center">
                  <p className="text-sm text-muted-foreground">No hiring role data available</p>
                </div>
              ) : (
                topJobs.map((job, index) => (
                  <div
                    key={`${job.title}-${index}`}
                    className="flex min-w-0 flex-col gap-4 rounded-lg bg-slate-50 p-3 sm:p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-600">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="break-words font-medium">{job.title || 'Unknown Role'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {job.applications} applications
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 sm:flex sm:gap-5 md:shrink-0">
                      <div className="min-w-[60px] text-center">
                        <p className="font-medium text-yellow-600">{job.pendingATS}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground sm:text-xs">Pending</p>
                      </div>
                      <div className="min-w-[60px] text-center">
                        <p className="font-medium text-green-600">{job.selected}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground sm:text-xs">Selected</p>
                      </div>
                      <div className="min-w-[60px] text-center">
                        <p className="font-medium text-red-600">{job.rejected}</p>
                        <p className="mt-1 text-[11px] text-muted-foreground sm:text-xs">Rejected</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex min-w-0 items-center justify-between">
              <div className="min-w-0">
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription className="mt-1">Platform events</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1 sm:max-h-[360px] sm:pr-2">
              {recentActivity.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                </div>
              ) : (
                recentActivity.map((activity, index) => {
                  const Icon = activity?.icon ?? getActivityIcon(activity);
                  const action = activity?.action ?? activity?.message ?? 'Platform activity';
                  const performedBy = activity?.performedBy;
                  const hasPerformer = Boolean(performedBy?.name || performedBy?.role);

                  return (
                    <div
                      key={
                        activity?.id ??
                        `${activity?.date ?? ''}-${activity?.time ?? ''}-${index}`
                      }
                      className="flex min-w-0 items-start gap-3 rounded-lg p-2 transition-colors hover:bg-slate-50 sm:p-3"
                    >
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm font-medium">{action}</p>

                        {activity?.status && (
                          <p className="mt-1 break-words text-xs text-muted-foreground">
                            Status:{' '}
                            <span className="font-medium text-foreground">{activity.status}</span>
                          </p>
                        )}

                        {activity?.candidateName && (
                          <p className="mt-1 break-words text-xs text-muted-foreground">
                            Candidate:{' '}
                            <span className="font-medium text-foreground">
                              {activity.candidateName}
                            </span>
                          </p>
                        )}

                        {hasPerformer && (
                          <p className="mt-1 break-words text-xs text-muted-foreground">
                            By:{' '}
                            <span className="font-medium text-foreground">
                              {performedBy?.name ?? 'Unknown'}
                            </span>
                            {performedBy?.role && <> • {performedBy.role}</>}
                          </p>
                        )}

                        <p className="mt-1 break-words text-xs text-muted-foreground">
                          {activity?.date ? `${activity.date} • ` : ''}
                          {activity?.time ?? ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="min-w-0">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/admin/jobs"
              className="group flex min-w-0 items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-slate-50"
            >
              <Briefcase className="h-7 w-7 shrink-0 text-blue-600 sm:h-8 sm:w-8" />
              <div className="min-w-0">
                <p className="font-medium">Manage Jobs</p>
                <p className="mt-1 text-xs text-muted-foreground">Create & edit positions</p>
              </div>
              <ArrowRight className="ml-auto h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              to="/admin/voice-agent"
              className="group flex min-w-0 items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-slate-50"
            >
              <Phone className="h-7 w-7 shrink-0 text-green-600 sm:h-8 sm:w-8" />
              <div className="min-w-0">
                <p className="font-medium">Voice Agent</p>
                <p className="mt-1 text-xs text-muted-foreground">Setup call questions</p>
              </div>
              <ArrowRight className="ml-auto h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>

            <Link
              to="/admin/master-db"
              className="group flex min-w-0 items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-slate-50 sm:col-span-2 lg:col-span-1"
            >
              <Database className="h-7 w-7 shrink-0 text-orange-600 sm:h-8 sm:w-8" />
              <div className="min-w-0">
                <p className="font-medium">Master DB</p>
                <p className="mt-1 text-xs text-muted-foreground">0 records</p>
              </div>
              <ArrowRight className="ml-auto h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}